<template>
  <div class="type-picker" ref="picker">
    <span class="picker-label" :class="{'required': selfContent.required}">{{ selfContent.label }}</span>
    <div class="picker-select" :class="{'is-open': isOpen, 'missing': isMissing, 'read-only': selfContent.readOnly}">
      <button type="button" class="picker-trigger" aria-haspopup="listbox" :aria-expanded="isOpen" :aria-label="selfContent.label" :disabled="selfContent.readOnly"
              @click="toggle" @keydown.esc.stop="close">
        <span class="option-badge" :class="{'is-empty': !selectedOption}">
          <svg v-if="selectedOption" class="option-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path v-for="path in optionIcons[selectedOption.value]" :key="path" :d="path"/>
          </svg>
        </span>
        <span class="trigger-label" :class="{'is-placeholder': !selectedOption}">{{ selectedOption ? selectedOption.label : 'Choisir un type de client' }}</span>
      </button>
      <ul v-if="isOpen" class="picker-menu" role="listbox" :aria-label="selfContent.label">
        <li v-for="option in selfContent.selectValues" :key="option.value" role="option" :aria-selected="option.value === selfContent.value">
          <button type="button" class="picker-option" :class="{'selected': option.value === selfContent.value}" :title="optionHints[option.value]"
                  @click="select(option.value)" @keydown.esc.stop="close">
            <span class="option-badge" :class="{'is-selected': option.value === selfContent.value}">
              <svg class="option-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path v-for="path in optionIcons[option.value]" :key="path" :d="path"/>
              </svg>
            </span>
            <span class="option-label">{{ option.label }}</span>
            <svg v-if="option.value === selfContent.value" class="option-check" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
// Aide affichée en info-bulle sur chaque type : les associations sont saisies comme des entreprises françaises
const OPTION_HINTS = {
  FRENCH_COMPANY: "Société, entreprise individuelle ou association.",
  INTERNATIONAL_COMPANY: "Établie hors de France.",
  INDIVIDUAL: "Agit à titre privé.",
  UNKNOWN: "À préciser plus tard."
};

// Pictogrammes (tracés SVG, trait de 1.8)
const OPTION_ICONS = {
  FRENCH_COMPANY: ['M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z', 'M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2', 'M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2', 'M10 6h4', 'M10 10h4', 'M10 14h4', 'M10 18h4'],
  INTERNATIONAL_COMPANY: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20', 'M2 12h20'],
  INDIVIDUAL: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
  UNKNOWN: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3', 'M12 17h.01']
};

export default {
  name: 'ClientTypePicker',
  props: ['content'],
  data() {
    return {
      selfContent: this.content,
      optionHints: OPTION_HINTS,
      optionIcons: OPTION_ICONS,
      isOpen: false
    };
  },
  computed: {
    selectedOption() {
      return (this.selfContent.selectValues || []).find(option => option.value === this.selfContent.value);
    },
    // Champ obligatoire encore vide
    isMissing() {
      const value = this.selfContent.value;
      return !!this.selfContent.required && !this.selfContent.readOnly && (value === '' || value === undefined || value === null);
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick);
  },
  methods: {
    toggle() {
      if (this.selfContent.readOnly) { return }
      this.isOpen ? this.close() : this.open();
    },
    open() {
      this.isOpen = true;
      document.addEventListener('mousedown', this.onOutsideClick);
    },
    close() {
      this.isOpen = false;
      document.removeEventListener('mousedown', this.onOutsideClick);
    },
    onOutsideClick(event) {
      if (this.$refs.picker && !this.$refs.picker.contains(event.target)) { this.close() }
    },
    select(value) {
      if (this.selfContent.readOnly) { return }
      this.selfContent.error = false;
      this.selfContent.value = value;
      this.close();
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.picker-label
  display block
  margin 0 0 6px 2px
  font-size 0.82em
  font-weight 600
  color CTRL_LABEL
  &.required::after
    content " *"
    color CTRL_ERROR

// Liste déroulante : même gabarit que les autres champs (hauteur, fond, bordure), avec le pictogramme du type
.picker-select
  position relative

.picker-trigger
  position relative
  display flex
  align-items center
  gap 10px
  box-sizing border-box
  width 100%
  height CTRL_HEIGHT
  // la marge de droite laisse la place au chevron
  padding 0 40px 0 6px
  font inherit
  text-align left
  line-height 1.2
  color CTRL_TEXT
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  background-color CTRL_BG
  cursor pointer
  transition background-color CTRL_EASE, border-color CTRL_EASE, box-shadow CTRL_EASE
  &:hover:not(:disabled)
    background-color CTRL_BG_HOVER
    border-color CTRL_BORDER_HOVER
  &:focus-visible
    outline none
    background-color CTRL_BG_FOCUS
    border-color CTRL_FOCUS
    box-shadow 0 0 0 3px CTRL_FOCUS_RING
  // Chevron
  &::before
    content ''
    position absolute
    top 50%
    right 18px
    margin-top -7px
    width 7px
    height 7px
    border-style solid
    border-width 0 2px 2px 0
    border-color CTRL_TEXT_MUTED
    border-radius 1px
    transform rotate(45deg)
    transition transform CTRL_EASE, margin-top CTRL_EASE, border-color CTRL_EASE
  &:disabled
    background-color CTRL_DISABLED_BG
    border-color transparent
    color CTRL_TEXT_MUTED
    cursor auto
    &::before
      border-color CTRL_DISABLED_TEXT

.picker-select.is-open .picker-trigger
  background-color CTRL_BG_FOCUS
  border-color CTRL_FOCUS
  box-shadow 0 0 0 3px CTRL_FOCUS_RING
  &::before
    margin-top -1px
    border-color CTRL_FOCUS
    transform rotate(-135deg)

.picker-select.missing:not(.is-open) .picker-trigger
  box-shadow 0 0 7px CTRL_MISSING_GLOW

.trigger-label
  flex 1 1 auto
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-weight 600
  &.is-placeholder
    font-weight 400
    color CTRL_PLACEHOLDER

// Pastille ronde du pictogramme : bleu pâle, bleu plein pour le type choisi dans la liste
.option-badge
  display flex
  align-items center
  justify-content center
  flex 0 0 auto
  width 28px
  height 28px
  border-radius 50%
  color THEME_COLOR_PRIMARY
  background-color rgba(THEME_COLOR_PRIMARY, 0.1)
  &.is-selected
    color THEME_COLOR_PRIMARY_OPPOSITE
    background-color THEME_COLOR_PRIMARY
  &.is-empty
    background-color transparent
    width 8px

.option-icon
  flex 0 0 auto

// Panneau blanc, bordure douce, ombre diffuse (comme CustomSelectField).
// Il s'ouvre dans le flux (et non par-dessus) : la modale ne le rogne pas et n'a pas besoin de défiler.
.picker-menu
  box-sizing border-box
  margin 5px 0 0
  padding 6px
  list-style none
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS_LG
  box-shadow 0 12px 28px -10px rgba(23, 35, 63, 0.22), 0 2px 8px rgba(23, 35, 63, 0.08)
  background-color white

.picker-option
  display flex
  align-items center
  gap 12px
  box-sizing border-box
  width 100%
  height 40px
  padding 0 10px
  font inherit
  font-weight 600
  text-align left
  color CTRL_TEXT
  background transparent
  border none
  border-radius CTRL_RADIUS_SM
  cursor pointer
  transition background-color CTRL_EASE
  &:hover
    background-color rgba(THEME_COLOR_PRIMARY, 0.08)
  &:focus-visible
    outline none
    box-shadow 0 0 0 2px CTRL_FOCUS
  &.selected
    color THEME_COLOR_PRIMARY
    background-color rgba(THEME_COLOR_PRIMARY, 0.1)

.option-label
  flex 1 1 auto
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.option-check
  flex 0 0 auto
</style>
